import type { Failure } from './failure'

export type NativeStoreErrorType =
  | 'interrupted'
  | 'pending'
  | 'product not found'
  | 'invalid transaction'
  | 'unknown'

export class NativeStoreError extends Error {
  constructor(message: NativeStoreErrorType) {
    super(message)
    this.name = 'NativeStoreError'
  }
}

/**
 * The promotional offer signature could not be fetched, so the purchase was
 * stopped before its order (ADR 0036). `cause` is the request's error.
 */
export class PromotionalOfferSignatureError extends Error {
  constructor(cause: unknown) {
    super('Promotional offer signature request failed', { cause })
    this.name = 'PromotionalOfferSignatureError'
  }
}

/**
 * Payment was taken, but a later step failed, so the subscription is not
 * linked. `cause` is that step's error.
 */
export class AfterPaymentError extends Error {
  public purchaseId: number
  public transactionId: string | null
  public node: string
  constructor(
    purchaseId: number,
    transactionId: string | null,
    node: string,
    cause?: unknown,
  ) {
    super('Purchase failed', { cause })
    this.name = 'AfterPaymentError'
    this.purchaseId = purchaseId
    this.transactionId = transactionId
    this.node = node
  }
}

/**
 * The store handed back a stale transaction again after every unclosed
 * subscription transaction was closed, so no payment was taken and the
 * purchase was cancelled (ADR 0045). `cause` is the second upload's 410.
 */
export class StaleTransactionError extends Error {
  public purchaseId: number
  public transactionId: string
  constructor(purchaseId: number, transactionId: string, cause: unknown) {
    super('Store handed back a stale transaction', { cause })
    this.name = 'StaleTransactionError'
    this.purchaseId = purchaseId
    this.transactionId = transactionId
  }
}

/**
 * The Receipt System answered the upload with 409 or 412: another Forest
 * account owns the subscription (a subscription conflict, ADR 0045).
 * `ownerUid` is that account's uid from the answer's `owner_uid`, or `null`
 * when the answer names none. `cause` is the HTTP error.
 */
export class SubscriptionConflictError extends Error {
  public ownerUid: number | null
  constructor(ownerUid: number | null, cause: unknown) {
    super(
      `Subscription owned by another Forest account (owner_uid: ${ownerUid ?? 'none'})`,
      { cause },
    )
    this.name = 'SubscriptionConflictError'
    this.ownerUid = ownerUid
  }
}

/**
 * A payment may have succeeded, but the page cannot yet prove it: a China
 * Android interruption that recovery has not confirmed (GLOSSARY.md, Purchase
 * confirmation dialog). The purchase stays open, so a late confirmation can
 * still complete it. `failure` is what the dialog shows (ADR 0037). `cause` is
 * the last failed request, if any.
 */
export class PurchaseConfirmationPendingError extends Error {
  public failure: Failure
  constructor(failure: Failure, cause?: unknown) {
    super('Purchase confirmation pending', { cause })
    this.name = 'PurchaseConfirmationPendingError'
    this.failure = failure
  }
}
